/* The heading is the trigger: profile-title type, chevron after it. Chrome only
   on hover so at rest it reads as a title. */
.trigger {
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  margin-left: calc(-1 * var(--space-2));
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-default);
}

.trigger:hover {
  background: color-mix(in srgb, var(--text-primary) 6%, transparent);
}

.trigger:focus-visible {
  outline: 2px solid var(--accent-secondary);
  outline-offset: 2px;
}

.label {
  font-family: var(--font-sans);
  font-size: var(--text-4xl, var(--text-3xl));
  font-weight: var(--weight-bold);
  line-height: 1.2;
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
  white-space: nowrap;
}

.chevron {
  flex-shrink: 0;
  color: var(--text-muted);
  transition: rotate var(--duration-fast) var(--ease-default), color var(--duration-fast);
}

/* Open (the lit replica): gold chevron flipped, gold rule under the name — d360 */
.trigger[data-open] {
  z-index: 2;
  border-radius: var(--radius-md) var(--radius-md) 0 0;
  box-shadow: inset 0 -2px 0 var(--accent-secondary);
}

.trigger[data-open]:hover {
  background: none;
}

.trigger[data-open] .chevron {
  rotate: 180deg;
  color: var(--accent-secondary-text);
}

/* ── Portal layer ── */

.layer {
  position: fixed;
  inset: 0;
  z-index: var(--z-overlay);
}

/* Dims the page so the list has focus */
.scrim {
  position: absolute;
  inset: 0;
  background: color-mix(in srgb, var(--bg-base) 72%, transparent);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  animation: fadeIn var(--duration-fast) var(--ease-default);
}

.panel {
  position: fixed;
  z-index: 2;
  display: flex;
  flex-direction: column;
  width: 340px;
  max-width: calc(100vw - 2 * var(--space-4));
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: dropIn var(--duration-fast) var(--ease-default);
}

.search {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: var(--space-2);
  padding: 0 var(--space-3);
  height: 40px;
  flex-shrink: 0;
  background: var(--bg-base);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  transition: border-color var(--duration-fast);
}

.search:focus-within {
  border-color: var(--border-strong);
}

.searchIcon {
  flex-shrink: 0;
  color: var(--text-muted);
}

.searchInput {
  all: unset;
  flex: 1;
  min-width: 0;
  font-family: var(--font-sans);
  font-size: var(--text-md);
  color: var(--text-primary);
}

.searchInput::placeholder {
  color: var(--text-muted);
}

/* Scrolls inside the panel; the panel's max-height keeps it on screen */
.list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: var(--space-2);
}

.section {
  padding: var(--space-3) var(--space-4) var(--space-1);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--text-muted);
}

.option {
  all: unset;
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  height: 48px;
  padding: 0 var(--space-4);
  cursor: pointer;
}

.option[data-active] {
  background: var(--bg-hover);
}

/* Current platform: crimson rail, as d360 and our other dropdowns */
.option[data-current]::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--accent-primary);
}

.optionIcon {
  flex-shrink: 0;
  border-radius: var(--radius-sm);
}

.optionName {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-md);
  color: var(--text-primary);
}

.option[data-current] .optionName {
  font-weight: var(--weight-semi);
}

.optionMeta {
  flex-shrink: 0;
  font-size: var(--text-sm);
  font-variant-numeric: tabular-nums;
  color: var(--text-muted);
}

.empty {
  margin: 0;
  padding: var(--space-4);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
}

@keyframes dropIn {
  from {
    opacity: 0;
    translate: 0 -4px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .scrim,
  .panel {
    animation: none;
  }
}
